<script setup>
import { onMounted, provide, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useUserStore } from './stores/user';
import { useI18n } from 'vue-i18n';

const userStore = useUserStore();
const router = useRouter();
const route = useRoute();
const { locale, t } = useI18n();

// Make the locale globally available
provide('locale', locale);

// Update document title based on route and language
const updateDocumentTitle = () => {
  // Get current route name without language prefix
  const routeName = route.name?.toString() || '';
  let baseRouteName = routeName.includes('-') ? routeName.split('-').pop() : routeName;
  
  // Set default title
  let title = t('common.appName');
  
  // Set specific titles based on route
  if (baseRouteName === 'home') {
    title = `${t('common.appName')} - ${t('common.tagline')}`;
  } else if (baseRouteName === 'login') {
    title = `${t('auth.login')} - ${t('common.appName')}`;
  } else if (baseRouteName === 'register') {
    title = `${t('auth.register')} - ${t('common.appName')}`;
  } else if (baseRouteName === 'voice-model') {
    title = `${t('navigation.voiceModel')} - ${t('common.appName')}`;
  } else if (baseRouteName === 'create-cover') {
    title = `${t('navigation.createCover')} - ${t('common.appName')}`;
  } else if (baseRouteName === 'my-covers') {
    title = `${t('navigation.myCovers')} - ${t('common.appName')}`;
  }
  
  // Update document title
  document.title = title;
};

// Watch for route or language changes to update title
watch(() => [route.name, locale.value], updateDocumentTitle, { immediate: true });

onMounted(async () => {
  // Check if user is already logged in
  try {
    await userStore.fetchUser();
  } catch (error) {
    console.error('Error fetching user:', error);
  }
  
  // Handle redirect from 404.html
  const urlParams = new URLSearchParams(window.location.search);
  const redirectParam = urlParams.get('redirect');
  
  if (redirectParam) {
    // Remove the redirect parameter from the URL
    const newUrl = window.location.pathname + 
                  window.location.search.replace(/[?&]redirect=[^&]+/, '') +
                  window.location.hash;
    
    window.history.replaceState({}, document.title, newUrl);
    
    // Navigate to the requested path
    const decodedPath = decodeURIComponent(redirectParam);
    if (decodedPath && decodedPath !== '/') {
      router.push(decodedPath);
    }
  }
  
  // Load user's preferred language
  const userLanguage = localStorage.getItem('user-language');
  if (userLanguage) {
    locale.value = userLanguage;
  }
  
  // Apply the html lang attribute
  document.documentElement.lang = locale.value;
});
</script>

<template>
  <router-view />
</template>

<style>
/* Base styles to complement Tailwind CSS */
:root {
  --apple-blue: #0071e3;
  --apple-blue-hover: #0077ed;
  --apple-gray: #f5f5f7;
  --apple-dark-text: #1d1d1f;
  --apple-light-text: #86868b;
  --apple-error: #ff3b30;
  --apple-success: #34c759;
  --apple-warning: #ff9500;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  font-family: 'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: var(--apple-gray);
  color: var(--apple-dark-text);
  line-height: 1.5;
  height: 100%;
}

/* Additional utility classes that might not be covered by Tailwind */
.rounded-apple-lg {
  border-radius: 12px;
}

.shadow-apple {
  box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.08);
}

.shadow-apple-lg {
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.12);
}

.bg-apple-blue {
  background-color: var(--apple-blue);
}

.bg-apple-gray {
  background-color: var(--apple-gray);
}

.focus-ring {
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 125, 250, 0.6);
}

/* Animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Smooth scrolling */
html {
  scroll-behavior: smooth;
}

/* Dark mode support - will be expanded as needed */
@media (prefers-color-scheme: dark) {
  :root {
    --apple-gray: #1a1a1a;
    --apple-dark-text: #f5f5f7;
    --apple-light-text: #a1a1a6;
  }
}
</style>
